/**
 * The admin API client (SW-601).
 *
 * Every call carries an App Bridge session token, which is what SW-202's middleware verifies.
 * A 401 triggers **one** silent token refresh and retry, invisible to the merchant, which is
 * the entire point: a token ages out mid-session on a perfectly healthy app, and showing an
 * error for that would be a self-inflicted support ticket.
 */

const BASE = '/admin/api'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** True when the failure is a plan gate rather than a fault, the UI upsells instead of apologising. */
  get isUpgradeRequired(): boolean {
    return this.status === 403 && this.code === 'upgrade_required'
  }
}

async function authorizedFetch(
  path: string,
  init: RequestInit,
  retrying = false,
): Promise<Response> {
  const token = await shopify.idToken()

  const response = await fetch(`${BASE}${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      authorization: `Bearer ${token}`,
      ...(init.body ? { 'content-type': 'application/json' } : {}),
    },
  })

  // The retry header is what the Worker sets alongside a 401. One retry only, a loop against
  // a genuinely rejected token would spin forever inside the merchant's admin.
  if (
    response.status === 401 &&
    !retrying &&
    response.headers.get('X-Shopify-Retry-Invalid-Session-Request')
  ) {
    return authorizedFetch(path, init, true)
  }

  return response
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await authorizedFetch(path, init)

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown>
    throw new ApiError(
      response.status,
      typeof body.error === 'string' ? body.error : 'request_failed',
      typeof body.message === 'string' ? body.message : `Request failed (${response.status})`,
      body,
    )
  }

  return response.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'POST',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: 'DELETE' }),

  /**
   * Downloads go through the same auth path, then hand the browser a blob. A plain `<a href>`
   * would hit the endpoint without a session token and get a 401.
   */
  async download(path: string, filename: string): Promise<void> {
    const response = await authorizedFetch(path, {})
    if (!response.ok) throw new ApiError(response.status, 'download_failed', 'Export failed')

    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(url)
  },
}
